import "@/App.css";
import "@/styles/googlePlaces.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import AdminBanner from "@/components/AdminBanner";
import MessageNotification from "@/components/MessageNotification";
import turboChatService from "@/utils/turboChatService";
import useScrollToTop from "@/utils/useScrollToTop";
import Home from "@/pages/Home";
import AvailableJobs from "@/pages/AvailableJobs";
import CrewDirectory from "@/pages/CrewDirectory";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import GetQuotes from "@/pages/GetQuotes";
import CancelLead from "@/pages/CancelLead";
import Dashboard from "@/pages/DashboardRedesigned";
import UpcomingBookings from "@/pages/UpcomingBookings";
import JobComms from "@/pages/JobComms";
import JobCommsDetail from "@/pages/JobCommsDetail";
import { JobCommsWebSocketProvider } from "@/contexts/JobCommsWebSocketContext";
import MessagesRedesigned from "@/pages/MessagesRedesigned";
import FindJobsRedesigned from "@/pages/FindJobsRedesigned";
import HireCrewRedesigned from "@/pages/HireCrewRedesigned";
import PostJobUltraModern from "@/pages/PostJobUltraModern";
import UpcomingPayments from "@/pages/UpcomingPayments";
import MyApplications from "@/pages/MyApplicationsRedesigned";
import Leads from "@/pages/Leads";
import InvoicesPrintable from "@/pages/InvoicesPrintable";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import CrewOnboarding from "@/pages/CrewOnboarding";
import PendingApproval from "@/pages/PendingApproval";
import HowItWorks from "@/pages/HowItWorks";
import AdminLogin from "@/pages/AdminLogin";
import AdminForgotPassword from "@/pages/AdminForgotPassword";
import AdminResetPassword from "@/pages/AdminResetPassword";
import AdminLayout from "@/components/AdminLayout";
import ReverifyAbnModal from "@/components/ReverifyAbnModal";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsers from "@/pages/AdminUsersRedesigned";
import AdminJobs from "@/pages/AdminJobs";
import AdminLeads from "@/pages/AdminLeads";
import AdminReports from "@/pages/AdminReports";
import AdminUserMap from "@/pages/AdminUserMap";
import AdminSettings from "@/pages/AdminSettings";
import AdminManagement from "@/pages/AdminManagement";
import AdminSystemMessages from "@/pages/AdminSystemMessages";
import AdminEmailTests from "@/pages/AdminEmailTests";
import AdminPolicies from "@/pages/AdminPolicies";
import AdminUpcomingPayments from "@/pages/AdminUpcomingPayments";
import AdminJobApproval from "@/pages/AdminJobApproval";
import AdminPaymentRelease from "@/pages/AdminPaymentRelease";
import AdminEmailLog from "@/pages/AdminEmailLog";
import AdminServices from "@/pages/AdminServices";
import AdminOfferAuditLog from "@/pages/AdminOfferAuditLog";
import AdminReviews from "@/pages/AdminReviews";
import AdminMessages from "@/pages/AdminMessages";
import OfferAccept from "@/pages/OfferAccept";
import OfferDecline from "@/pages/OfferDecline";
import JobDetails from "@/pages/JobDetails";
import NotFound from "@/pages/NotFound";
import { Toaster } from "@/components/ui/sonner";
import { TOKEN_KEY, clearSession, createUnauthorizedHandler, storeRefreshToken } from "@/utils/authSession";

// Scroll to top component - Enhanced for mobile/iPad
function ScrollToTop() {
  useScrollToTop();
  return null;
}


// Dynamic Stripe initialization - fetches key from backend based on mode
const stripePromise = (async () => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://shootzu.com';
    const response = await fetch(`${backendUrl}/api/stripe/config`);
    const data = await response.json();
    if (data.publishableKey) {
      return loadStripe(data.publishableKey);
    }
    return loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
  } catch (error) {
    return loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
  }
})();

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem('shootzu_token') !== null
  );
  const [isAdmin, setIsAdmin] = useState(
    localStorage.getItem('shootzu_admin') !== null
  );
  const [user, setUser] = useState(() => {
    const userStr = localStorage.getItem('shootzu_user');
    return userStr ? JSON.parse(userStr) : null;
  });

  // Poll for authentication changes (for admin login as user)
  useEffect(() => {
    const checkAuth = () => {
      const hasToken = localStorage.getItem('shootzu_token') !== null;
      const hasAdmin = localStorage.getItem('shootzu_admin') !== null;
      
      if (hasToken !== isAuthenticated) {
        setIsAuthenticated(hasToken);
      }
      if (hasAdmin !== isAdmin) {
        setIsAdmin(hasAdmin);
      }
    };
    
    // Check every 500ms for auth changes
    const interval = setInterval(checkAuth, 500);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, isAdmin]);

  // Load Turbo Chat widget with the correct site ID based on auth state
  // Public pages: caa78d1b  |  Dashboard: 90eea063
  useEffect(() => {
    const PUBLIC_SITE_ID = 'caa78d1b-6f3f-4bfd-a30c-ef9bca2d3499';
    const DASHBOARD_SITE_ID = '90eea063-bba4-4b3d-b9ec-9c87b3cef5b6';

    // Remove existing widget elements
    const oldScript = document.getElementById('turbo-chat-script');
    const oldBtn = document.getElementById('turbo-chat-btn');
    const oldContainer = document.getElementById('turbo-chat-container');
    if (oldScript) oldScript.remove();
    if (oldBtn) oldBtn.remove();
    if (oldContainer) oldContainer.remove();
    delete window.TurboChat;

    const siteId = isAuthenticated ? DASHBOARD_SITE_ID : PUBLIC_SITE_ID;

    // Load the widget script
    const script = document.createElement('script');
    script.id = 'turbo-chat-script';
    script.src = 'https://chat.turbo.net.au/widget.js';
    script.async = true;
    script.setAttribute('data-site-id', siteId);
    if (isAuthenticated) {
      script.setAttribute('data-hide-button', 'true');
      try {
        const userData = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
        const visitorName = [userData.firstName, userData.lastName].filter(Boolean).join(' ') || userData.email?.split('@')[0] || '';
        if (visitorName) script.setAttribute('data-visitor-name', visitorName);
        if (userData.email) script.setAttribute('data-visitor-email', userData.email);
        if (userData.profile_photo_url) script.setAttribute('data-visitor-photo', userData.profile_photo_url);
      } catch (e) { /* ignore */ }
    }
    document.body.appendChild(script);

    if (isAuthenticated) {
      document.body.classList.add('hide-chat-btn');

      // Position chat window next to sidebar once widget loads
      const positionChat = () => {
        const container = document.getElementById('turbo-chat-container');
        if (container) {
          // Anchor to bottom-left, next to sidebar
          container.style.bottom = '60px';
          container.style.right = 'auto';
          container.style.left = '260px';
        }
      };
      // Try immediately and observe for when widget injects the container
      positionChat();
      const obs = new MutationObserver(() => {
        if (document.getElementById('turbo-chat-container')) {
          positionChat();
          obs.disconnect();
        }
      });
      obs.observe(document.body, { childList: true });
      setTimeout(() => obs.disconnect(), 10000);

      const userDataString = localStorage.getItem('shootzu_user');
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          turboChatService.initializeWithUser(userData);
        } catch (error) {
          console.error('Failed to parse user data for Turbo Chat', error);
        }
      }
    } else {
      document.body.classList.remove('hide-chat-btn');
      turboChatService.reset();
    }
  }, [isAuthenticated]);

  // Drop the customer session and send the user to login. Only uses state setters, so
  // it is safe to capture in the mount-only effects below.
  const endSession = (suspended) => {
    clearSession();
    setUser(null);
    setIsAuthenticated(false);
    if (!window.location.pathname.includes('/login')) {
      window.location.href = suspended ? '/login?error=account_suspended' : '/login?expired=true';
    }
  };

  // Validate session on app load - check if stored token is still valid
  useEffect(() => {
    const validateSession = async () => {
      const token = localStorage.getItem('shootzu_token');

      // Skip validation for admin impersonation tokens (they don't have real sessions)
      if (!token || token.startsWith('admin_viewing_as_')) {
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://shootzu.com';
        const meRes = await axios.get(`${backendUrl}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // Session is valid — refresh the stored user from the canonical doc so
        // server-set flags (e.g. abn_reverification_required) are picked up even
        // for users who were already logged in before the flag was applied.
        if (meRes?.data && meRes.data.id) {
          try {
            const stored = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
            const merged = { ...stored, ...meRes.data };
            localStorage.setItem('shootzu_user', JSON.stringify(merged));
            setUser(merged);
          } catch (_) { /* ignore parse errors */ }
        }
      } catch (error) {
        // The global interceptor below already tried a refresh and, if that failed,
        // ended the session. This only catches a 401 it didn't handle.
        if (error.response?.status === 401 && localStorage.getItem(TOKEN_KEY)) {
          console.log('Session invalid on app load - logging out');
          endSession(false);
        }
      }
    };

    validateSession();
  }, []); // Run once on mount

  // Global axios interceptor to handle 401 (expired/invalid token) errors: try a silent
  // refresh and replay the request first; only end the session if that fails.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      createUnauthorizedHandler({ client: axios, endSession })
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // refreshToken: pass it (or null) from every login path. A null clears any refresh
  // token left by a previous user; omitting the argument leaves storage untouched.
  const handleLogin = (token, userData, refreshToken) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem('shootzu_user', JSON.stringify(userData));
    if (refreshToken !== undefined) storeRefreshToken(refreshToken);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleUpdateUser = (updatedUser) => {
    localStorage.setItem('shootzu_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const handleLogout = () => {
    clearSession();
    setUser(null);
    setIsAuthenticated(false);
  };

  const handleAdminLogin = () => {
    setIsAdmin(true);
  };

  return (
    <Elements stripe={stripePromise}>
      <ThemeProvider>
        <SidebarProvider>
        <div className="App">
          <Toaster position="top-right" />
          {/* Non-dismissible ABN re-verification blocker — shown on login to users
              whose ABN was detected as cancelled/invalid against the ABR. Suppressed
              during admin impersonation (admin_viewing_as_ tokens). */}
          {isAuthenticated &&
            user?.abn_reverification_required === true &&
            !(localStorage.getItem('shootzu_token') || '').startsWith('admin_viewing_as_') && (
              <ReverifyAbnModal
                userId={user.id}
                reason={user.abn_reverification_reason}
                onCleared={(updatedUser) => handleUpdateUser(updatedUser)}
              />
            )}
          <BrowserRouter>
            <ScrollToTop />
            <AdminBanner />
            <MessageNotification />
            <JobCommsWebSocketWrapper isAuthenticated={isAuthenticated} userId={user?.id}>
            <Routes>
          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />
          } />
          <Route path="/signup" element={
            isAuthenticated ? <Navigate to="/dashboard" /> : <Signup onLogin={handleLogin} />
          } />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/getquotes" element={<GetQuotes />} />
          <Route path="/cancel-lead/:token" element={<CancelLead />} />
          <Route path="/offer/accept" element={<OfferAccept />} />
          <Route path="/offer/decline" element={<OfferDecline />} />
          <Route path="/jobs/:jobId" element={<JobDetails onLogout={handleLogout} />} />
          <Route path="/dashboard" element={
            isAuthenticated ? <Dashboard onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/upcoming-bookings" element={
            isAuthenticated ? <Navigate to="/job-comms" replace /> : <Navigate to="/login" />
          } />
          <Route path="/job-comms" element={
            isAuthenticated ? <JobComms onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/job-comms/:jobId" element={
            isAuthenticated ? <JobCommsDetail onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/messages" element={
            isAuthenticated ? <MessagesRedesigned onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/find-jobs" element={
            isAuthenticated ? <FindJobsRedesigned onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/hire-crew" element={
            isAuthenticated ? <HireCrewRedesigned onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          {/* Redirect old job format routes to unified post-job */}
          <Route path="/select-job-format" element={<Navigate to="/post-job" replace />} />
          <Route path="/post-job-simple" element={<Navigate to="/post-job" replace />} />
          <Route path="/post-job" element={
            isAuthenticated ? <PostJobUltraModern onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/applications" element={
            isAuthenticated ? <MyApplications onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/leads" element={
            isAuthenticated ? <Leads onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/leads/:leadId" element={<Navigate to="/leads" replace />} />
          <Route path="/invoices" element={
            isAuthenticated ? <InvoicesPrintable onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/upcoming-payments" element={
            isAuthenticated ? <UpcomingPayments onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/profile" element={
            isAuthenticated ? <Profile onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/settings" element={
            isAuthenticated ? <Settings onLogout={handleLogout} /> : <Navigate to="/login" />
          } />
          <Route path="/onboarding" element={
            isAuthenticated ? <CrewOnboarding user={user} onUpdateUser={handleUpdateUser} /> : <Navigate to="/login" />
          } />
          <Route path="/pending-approval" element={
            isAuthenticated ? <PendingApproval user={user} /> : <Navigate to="/login" />
          } />

          {/* Admin Routes — outside layout (no sidebar) */}
          <Route path="/szadmin" element={<AdminLogin onAdminLogin={handleAdminLogin} />} />
          <Route path="/szadmin/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/szadmin/reset-password" element={<AdminResetPassword />} />

          {/* Admin Routes — inside layout (with sidebar) */}
          <Route element={<AdminLayout />}>
            <Route path="/szadmin/dashboard" element={<AdminDashboard />} />
            <Route path="/szadmin/users" element={<AdminUsers />} />
            <Route path="/szadmin/jobs" element={<AdminJobs />} />
            <Route path="/szadmin/leads" element={<AdminLeads />} />
            <Route path="/szadmin/reports" element={<AdminReports />} />
            <Route path="/szadmin/user-map" element={<AdminUserMap />} />
            <Route path="/szadmin/settings" element={<AdminSettings />} />
            <Route path="/szadmin/admins" element={<AdminManagement />} />
            <Route path="/szadmin/messages" element={<AdminMessages />} />
            <Route path="/szadmin/system-messages" element={<AdminSystemMessages />} />
            <Route path="/szadmin/email-tests" element={<AdminEmailTests />} />
            <Route path="/szadmin/policies" element={<AdminPolicies />} />
            <Route path="/szadmin/payments" element={<AdminUpcomingPayments />} />
            <Route path="/szadmin/job-approval" element={<AdminJobApproval />} />
            <Route path="/szadmin/payment-release" element={<AdminPaymentRelease />} />
            <Route path="/szadmin/email-log" element={<AdminEmailLog />} />
            <Route path="/szadmin/services" element={<AdminServices />} />
            <Route path="/szadmin/offer-audit-log" element={<AdminOfferAuditLog />} />
            <Route path="/szadmin/reviews" element={<AdminReviews />} />
          </Route>

          {/* Public Marketing Pages */}
          <Route path="/home" element={<Home />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/available-jobs" element={<AvailableJobs />} />
          <Route path="/crew" element={<CrewDirectory />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          
          <Route path="*" element={<NotFound />} />
          
          <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Home />} />
        </Routes>
            </JobCommsWebSocketWrapper>
      </BrowserRouter>
    </div>
    </SidebarProvider>
    </ThemeProvider>
  </Elements>
  );
}

// Wrapper that conditionally provides WebSocket context
function JobCommsWebSocketWrapper({ isAuthenticated, userId, children }) {
  if (isAuthenticated && userId) {
    return (
      <JobCommsWebSocketProvider userId={userId}>
        {children}
      </JobCommsWebSocketProvider>
    );
  }
  return children;
}

export default App;